import { useEffect, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { PurchasableItem, SharedProps } from '@/types'

const ACCOUNT_OPT_IN_TEXT =
  'Never lose your tickets, keep track of donations, get notified of event updates and more when you become a philanthropist'

type Line = { item: PurchasableItem; quantity: number }

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  lines: Line[]
  onSoldOut?: (purchasableItemId: string, remaining: number | null, name: string) => void
}

type ServerResponse = {
  redirect_to?: string
  purchase_id?: string
  error?: string
  purchasable_item_id?: string
  purchasable_item_name?: string
  remaining?: number | null
  max_per_buyer?: number
}

export default function FreeCheckoutModal({ open, onOpenChange, lines, onSoldOut }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [wantsAccount, setWantsAccount] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const showAccountOptIn = !current_user

  useEffect(() => {
    if (!open) {
      setSubmitting(false)
      setError(null)
      setWantsAccount(true)
    }
  }, [open])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
      const body: Record<string, unknown> = {
        items: lines.map((l) => ({ purchasable_item_id: l.item.id, quantity: l.quantity })),
        buyer: { name: name.trim(), email: email.trim(), phone: phone.trim() || null },
      }
      if (showAccountOptIn) body.wants_account = wantsAccount
      const res = await fetch('/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify(body),
      })
      const data = (await res.json()) as ServerResponse
      if (data.redirect_to) {
        window.location.assign(data.redirect_to)
        return
      }
      if (data.error === 'sold_out' && data.purchasable_item_id) {
        onSoldOut?.(data.purchasable_item_id, data.remaining ?? null, data.purchasable_item_name ?? 'A tier')
        onOpenChange(false)
        return
      }
      if (data.error === 'over_max_per_buyer') {
        const limit = data.max_per_buyer
        toast.error(
          `${data.purchasable_item_name ?? 'This tier'} is limited to ${limit} per transaction.`,
        )
        onOpenChange(false)
        return
      }
      if (data.error === 'buyer_required') {
        setError('Please enter your name and email to claim free tickets.')
      } else {
        setError("We couldn't complete your claim. Try again or contact the organizer.")
      }
    } catch {
      setError("We couldn't complete your claim. Try again or contact the organizer.")
    }
    setSubmitting(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Claim your free tickets</DialogTitle>
          <DialogDescription>
            We'll send your ticket QR codes to this email.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="free_name">Full name *</Label>
            <Input
              id="free_name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="free_email">Email *</Label>
            <Input
              id="free_email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="free_phone">Phone (optional)</Label>
            <Input
              id="free_phone"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          {showAccountOptIn && (
            <label className="flex items-start gap-2.5 rounded-lg border border-zinc-950/10 bg-muted/30 p-3 text-sm">
              <Checkbox
                checked={wantsAccount}
                onCheckedChange={(v) => setWantsAccount(v === true)}
                aria-label="Create an account after claiming"
                className="mt-0.5"
              />
              <span className="text-foreground">{ACCOUNT_OPT_IN_TEXT}</span>
            </label>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Claiming…' : 'Claim free tickets'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
